import { faSave, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import EmptyList from 'components/common/EmptyList';
import Flex from 'components/common/Flex';
import DialogModal from 'components/modals/DialogModal';
import Pagination from 'components/pagination';
import { formatDateTime } from 'helpers/utils';
import useAdvanceTable from 'hooks/useAdvanceTable';
import useNotification from 'hooks/useNotification';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import BillingService from 'services/Billing.service';
import InvoiceService from 'services/Invoice.service';
import TicketBillableItemService from 'services/TicketBillableItem.service';

const GroupItemsLoader = ({
  groupType,
  groupId,
  filters,
  onGenerateInvoice
}) => {
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState({
    content: [],
    totalElements: 0,
    totalPages: 0,
    size: 20,
    number: 0,
    totalElementsPerPage: 20,
    hasNextPage: false
  });
  const [itemFilters, setItemFilters] = useState({
    page: 0,
    size: 20
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  const formatCurrency = value => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const getBillingStatusBadge = status => {
    const variants = {
      PENDING: 'warning',
      INVOICED: 'info',
      COMPLETED: 'success',
      CANCELED: 'danger'
    };
    const labels = {
      PENDING: 'Pendente',
      INVOICED: 'Faturado',
      COMPLETED: 'Concluído',
      CANCELED: 'Cancelado'
    };
    return (
      <Badge bg={variants[status] || 'secondary'}>
        {labels[status] || status}
      </Badge>
    );
  };

  const columns = [
    {
      accessorKey: 'customer.name',
      header: 'Cliente',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const customerName = item.customer.name;
        return <div>{customerName}</div>;
      }
    },
    {
      accessorKey: 'ticket.code',
      header: 'Ticket',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const ticketCode = item.ticket.code;
        return <Link to={`/tickets/${item.ticket.id}`}>#{ticketCode}</Link>;
      }
    },
    {
      accessorKey: 'productService.name',
      header: 'Item',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const productServiceName = item.productService.name;
        return <div>{productServiceName || '-'}</div>;
      }
    },
    {
      accessorKey: 'quantity',
      header: 'Qtd',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { quantity } = rowData.row.original;
        return <div>{quantity}</div>;
      }
    },
    {
      accessorKey: 'unitPrice',
      header: 'Preço Unit.',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { unitPrice } = rowData.row.original;
        return <div>{formatCurrency(unitPrice)}</div>;
      }
    },
    {
      accessorKey: 'totalPrice',
      header: 'Preço Total',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { totalPrice } = rowData.row.original;
        return <div className="fw-bold">{formatCurrency(totalPrice)}</div>;
      }
    },
    {
      accessorKey: 'launchedAt',
      header: 'Data Lançamento',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { launchedAt } = rowData.row.original;
        return <div>{formatDateTime(launchedAt)}</div>;
      }
    },
    {
      accessorKey: 'billingStatus',
      header: 'Status',
      meta: {
        headerProps: { className: 'text-900' }
      },
      cell: rowData => {
        const { billingStatus } = rowData.row.original;
        return getBillingStatusBadge(billingStatus);
      }
    },
    {
      accessorKey: 'actions',
      header: 'Ações',
      meta: {
        headerProps: { className: 'text-900 text-center' },
        cellProps: { className: 'text-center' }
      },
      cell: rowData => {
        const item = rowData.row.original;
        const ticketId = item.ticket.id;
        const itemId = item.id;
        const billingStatus = item.billingStatus || item.billing_status;
        const canDelete = billingStatus === 'PENDING';
        return (
          <Flex justifyContent="center" alignItems="center" className="gap-2">
            <Button
              variant="link"
              size="sm"
              className="p-1"
              onClick={e => {
                e.stopPropagation();
                setItemToDelete({ ticketId, itemId });
                setShowDeleteDialog(true);
              }}
              disabled={!canDelete || isDeleting}
              title={
                canDelete
                  ? 'Remover item'
                  : 'Apenas itens pendentes podem ser removidos'
              }
            >
              <FontAwesomeIcon icon={faTrash} className="text-danger" />
            </Button>
          </Flex>
        );
      }
    }
  ];

  const loadItems = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams({
        page: itemFilters.page,
        size: itemFilters.size,
        billingStatus: filters.billingStatus || 'PENDING'
      }).toString();

      let response;
      if (groupType === 'customer') {
        response = await BillingService.getBillableItemsByCustomer(
          groupId,
          queryParams
        );
      } else if (groupType === 'ticket') {
        response = await BillingService.getBillableItemsByTicket(
          groupId,
          queryParams
        );
      }

      if (response && response.content) {
        setItems({
          ...response,
          totalElementsPerPage: response.size || 20,
          hasNextPage: response.number < response.totalPages - 1
        });
      }
    } catch (err) {
      console.error('Erro ao carregar itens do grupo:', err);
      setError('Erro ao carregar itens. Tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (groupId) {
      loadItems();
    }
  }, [groupId, itemFilters, filters.billingStatus]);

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setItemToDelete(null);
  };

  const handleConfirmDelete = async confirmed => {
    if (!confirmed) {
      handleCloseDeleteDialog();
      return;
    }

    if (!itemToDelete) {
      handleCloseDeleteDialog();
      return;
    }

    setIsDeleting(true);
    try {
      const response = await TicketBillableItemService.delete(
        itemToDelete.ticket.id,
        itemToDelete.itemId
      );

      if (
        response.status === 200 ||
        response.status === 204 ||
        !response.userMessage
      ) {
        showSuccessNotification('Item removido com sucesso');
        // Limpar seleção se o item deletado estava selecionado
        const updatedSelectedItems = selectedItems.filter(
          id => id !== itemToDelete.itemId
        );
        setSelectedItems(updatedSelectedItems);
        if (updatedSelectedItems.length === 0) {
          setSelectedCustomerId(null);
          table.setRowSelection({});
        }
        loadItems();
      } else {
        showErrorNotification(response.userMessage || 'Erro ao remover item');
      }
    } catch (error) {
      console.error('Erro ao remover item:', error);
      showErrorNotification('Erro ao remover item');
    } finally {
      setIsDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  const handleGenerateInvoice = async () => {
    if (selectedItems.length === 0) return;

    if (!selectedCustomerId) {
      showErrorNotification(
        'Selecione itens de um cliente para gerar a fatura'
      );
      return;
    }

    setIsGeneratingInvoice(true);
    try {
      const response = await InvoiceService.create({
        billableItemIds: selectedItems,
        customerId: selectedCustomerId
      });

      if (response.id || response.status === 201 || response.status === 200) {
        showSuccessNotification('Fatura gerada com sucesso!');
        setSelectedItems([]);
        setSelectedCustomerId(null);
        // Limpar seleção da tabela
        table.setRowSelection({});
        // Recarregar dados
        loadItems();
        // Chamar callback do componente pai se fornecido
        if (onGenerateInvoice) {
          onGenerateInvoice();
        }
      } else {
        showErrorNotification(response.userMessage || 'Erro ao gerar fatura');
      }
    } catch (error) {
      console.error('Erro ao gerar fatura:', error);
      showErrorNotification('Erro ao gerar fatura. Tente novamente.');
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const tableData = useMemo(() => {
    return items.content;
  }, [items.content]);

  // Função para verificar se um item pode ser selecionado
  const canSelectItem = useMemo(() => {
    return item => {
      // Apenas itens pendentes podem ser selecionados
      const billingStatus = item.billingStatus || item.billing_status;
      if (billingStatus !== 'PENDING') return false;
      if (selectedItems.length === 0) return true;
      if (selectedCustomerId === null) return true;
      const customerId = item.customer.id;
      return customerId === selectedCustomerId;
    };
  }, [selectedItems.length, selectedCustomerId]);

  // Modificar dados da tabela para incluir informação de seleção
  const tableDataWithSelection = useMemo(() => {
    return tableData.map(item => ({
      ...item,
      canSelect: canSelectItem(item)
    }));
  }, [tableData, canSelectItem]);

  const table = useAdvanceTable({
    data: tableDataWithSelection,
    columns,
    selection: true,
    selectionColumnWidth: 50,
    getRowCanSelect: row => {
      return row.original.canSelect === true;
    }
  });

  // Atualizar selectedItems quando a seleção na tabela mudar
  useEffect(() => {
    const selectedRows = table.getSelectedRowModel().rows;
    const selectedIds = selectedRows.map(row => row.original.id);

    // Filtrar apenas itens que podem ser selecionados
    const validSelectedIds = selectedIds.filter(id => {
      const item = items.content.find(item => item.id === id);
      return item && canSelectItem(item);
    });

    setSelectedItems(validSelectedIds);

    // Determinar o cliente selecionado baseado nos itens selecionados
    if (validSelectedIds.length > 0) {
      const firstSelectedItem = items.content.find(
        item => item.id === validSelectedIds[0]
      );
      if (firstSelectedItem) {
        const customerId = firstSelectedItem.customer.id;
        setSelectedCustomerId(customerId);
      }
    } else {
      setSelectedCustomerId(null);
    }
  }, [table.getState().rowSelection, table, items.content, canSelectItem]);

  // Interceptar seleção de linhas para aplicar regra de negócio
  useEffect(() => {
    if (
      table &&
      tableDataWithSelection.length > 0 &&
      selectedCustomerId !== null
    ) {
      const currentSelection = table.getState().rowSelection || {};
      const newSelection = {};

      Object.keys(currentSelection).forEach(rowIndex => {
        const row = tableDataWithSelection[parseInt(rowIndex)];
        if (row && canSelectItem(row)) {
          newSelection[rowIndex] = true;
        }
      });

      // Se houver diferença, atualizar seleção
      if (JSON.stringify(currentSelection) !== JSON.stringify(newSelection)) {
        table.setRowSelection(newSelection);
      }
    }
  }, [table, tableDataWithSelection, selectedCustomerId, canSelectItem]);

  const isEmpty = !isLoading && items.content.length === 0;

  if (error) {
    return (
      <Alert variant="danger" className="mt-3">
        {error}
      </Alert>
    );
  }

  return (
    <div className="mt-3">
      {isLoading ? (
        <Flex justifyContent="center" className="py-4">
          <Spinner animation="border" variant="primary" />
        </Flex>
      ) : isEmpty ? (
        <EmptyList imgSize={300} vhSize={50} />
      ) : (
        <>
          <Flex justifyContent="between" alignItems="center" className="mb-3">
            <div></div>
            <Flex alignItems="center" className="gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleGenerateInvoice}
                disabled={selectedItems.length === 0 || isGeneratingInvoice}
              >
                <FontAwesomeIcon icon={faSave} className="me-2" />
                {isGeneratingInvoice ? 'Gerando...' : 'Gerar Fatura'}
              </Button>
            </Flex>
          </Flex>
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              headerClassName="text-nowrap align-middle"
              rowClassName="align-middle white-space-nowrap"
              tableProps={{
                striped: true,
                className: 'fs-10 mb-0 overflow-hidden'
              }}
            />
          </AdvanceTableProvider>
          <Pagination
            data={items}
            filters={itemFilters}
            setFilters={setItemFilters}
          />
        </>
      )}
      <DialogModal
        message="Tem certeza que deseja remover este item faturável?"
        showModal={showDeleteDialog}
        handleClose={handleCloseDeleteDialog}
        response={handleConfirmDelete}
      />
    </div>
  );
};

export default GroupItemsLoader;
